import { api } from '@mbfab/client-api'
import { isNull, isSome } from '@mbfab/shared'
import { createFileRoute } from '@tanstack/react-router'

import { AdminSidebarContainer } from '@/components/app/admin-sidebar-container'
import { CenteredLoading } from '@/components/app/centered-loading'
import { ErrorPanel } from '@/components/app/error-panel'
import { AdminTenantDetail } from '@/components/screens/admin-tenant-detail'
import { userFacingErrorMessage } from '@/lib/user-facing-error-message'

export const Route = createFileRoute('/admin/tenants/$id')({
  component: Page,
})

function Page() {
  const { id } = Route.useParams()
  return (
    <div className="flex min-h-screen bg-background">
      <AdminSidebarContainer />
      <main className="min-w-0 flex-1 px-4 py-6 sm:px-6 lg:px-8 xl:px-10">
        <TenantDetailContainer id={id} />
      </main>
    </div>
  )
}

function TenantDetailContainer({ id }: { id: string }) {
  const client = api.useClient()
  const tenantQuery = api.useFetchTenantQuery({ variables: { id } })
  const plansQuery = api.useFetchAdminPlansQuery()
  const conversionsQuery = api.useFetchAdminShapeNameConversionsQuery({ variables: { tenantId: id } })

  // 3クエリのいずれかが初回取得できていなければ読み込み中/エラー扱いにする。
  // 一度表示できた後の refetch 失敗ではエラーパネルに差し替えない（users.tsx と同じ考え方）
  if (isNull(tenantQuery.data) || isNull(plansQuery.data) || isNull(conversionsQuery.data)) {
    if (isSome(tenantQuery.error) || isSome(plansQuery.error) || isSome(conversionsQuery.error)) {
      return <ErrorPanel title="テナントの取得に失敗しました" description="しばらくしてから再度お試しください。" />
    }
    return <CenteredLoading message="読み込み中..." />
  }

  // mutation 本体と再取得は try を分ける（users.tsx と同じ理由。
  // refetch 失敗を「操作の失敗」として報告しない）
  const runMutation = async (
    mutate: () => Promise<unknown>,
    refetch: () => Promise<unknown>,
    failureMessage: string,
  ): Promise<{ ok: true } | { ok: false; message: string }> => {
    try {
      await mutate()
    } catch (error) {
      return { ok: false, message: userFacingErrorMessage(error, failureMessage) }
    }
    try {
      await refetch()
    } catch {
      // 再取得の失敗を「操作の失敗」として報告しない
    }
    return { ok: true }
  }

  return (
    <AdminTenantDetail
      tenant={tenantQuery.data.adminTenant}
      plans={plansQuery.data.adminPlans}
      conversions={conversionsQuery.data.adminShapeNameConversions}
      onUpdateTenant={async (input) =>
        await runMutation(
          async () => await client.updateTenant({ tenant: input }),
          async () => await tenantQuery.refetch(),
          'テナントの更新に失敗しました',
        )
      }
      onInviteUser={async (input) =>
        await runMutation(
          async () => await client.createAdminInvitation({ input: { ...input, tenantId: id } }),
          async () => await tenantQuery.refetch(),
          '招待に失敗しました',
        )
      }
      onDeleteUser={async (userId) =>
        await runMutation(
          async () => await client.deleteAdminUser({ id: userId }),
          async () => await tenantQuery.refetch(),
          '削除に失敗しました',
        )
      }
      onUpsertSetting={async (input) =>
        await runMutation(
          async () => await client.upsertAdminTenantSetting({ tenantSetting: input }),
          async () => await tenantQuery.refetch(),
          '設定の更新に失敗しました',
        )
      }
      onUpsertConversion={async (input) =>
        await runMutation(
          async () => await client.upsertAdminShapeNameConversion({ input }),
          async () => await conversionsQuery.refetch(),
          '変換の保存に失敗しました',
        )
      }
      onDeleteConversion={async (conversionId) =>
        await runMutation(
          async () => await client.deleteAdminShapeNameConversion({ tenantId: id, id: conversionId }),
          async () => await conversionsQuery.refetch(),
          '変換の削除に失敗しました',
        )
      }
    />
  )
}
